import { createContext, ReactNode, useContext, useMemo } from 'react';
import {
  CreditCheckResultEnum,
  TvInternetOffer,
  ServiceabilityProvider,
  HomeServicesPreferences,
  HomeServicesAddress,
} from '@updater/consumer-graph';
import type { ActivationDefaults as ActivationDefaultsType } from 'flows/tvi/hooks/use-get-estimated-activation-date';
import { ThemeWithAssets } from 'flows/tvi/types/theme';

// Re-export ActivationDefaults for convenience
export type { ActivationDefaults } from 'flows/tvi/hooks/use-get-estimated-activation-date';

/**
 * Customer information - either from guest reservation token or authenticated user
 */
export interface CustomerInfo {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  address?: {
    street?: string;
    unit?: string;
    city?: string;
    state?: string;
    postalCode?: string; // Matches GraphQL schema field name
  };
  /** Previous address (fromAddress) - used for credit card prefill in authenticated mode */
  fromAddress?: Address;
}

/**
 * Address information for installation
 */
export interface Address {
  street: string;
  city: string;
  state: string;
  postalCode: string; // Matches GraphQL schema field name
  unit: string;
}

/**
 * Credit check data for Xfinity provider
 * Only populated in authenticated mode when credit check has been performed
 */
export interface CreditCheckData {
  /** Result of credit check */
  creditCheckResult?: CreditCheckResultEnum;
  /** Tokenized SSN */
  ssnPiiToken?: string;
  /** Tokenized date of birth */
  dobPiiToken?: string;
}

export interface GuestConfig {
  source?: string;
  theme?: string; // deprecated
  supportPhoneNumber?: string;
  themeWithAssets?: ThemeWithAssets;
}

/**
 * Reservation interface for buy flow
 * Structurally compatible with ui-widgets UseReservedOfferType to support both guest and authenticated modes
 *
 * In authenticated mode: populated from GraphQL via ui-widgets
 * In guest mode: populated from reservation token payload via parseReservationToken
 */
export interface Reservation {
  /**
   * Reservation metadata
   * Matches structure from ui-widgets useReservation hook
   */
  reservationInfo?: {
    code?: string;
    status?: string;
    preferences?: Partial<HomeServicesPreferences> | null;
    toAddress?: HomeServicesAddress | null;
    requestId?: string;
    leadId?: string;
    qualification?: {
      optionalFieldsById?: {
        gclid?: string;
        partnerPropertyId?: string;
        partnerUserUuid?: string;
      };
      promoId?: string;
      userEmail?: string;
      gtmCode?: string;
    };
  };

  /**
   * Offer details
   * Uses TvInternetOffer from GraphQL schema
   * All fields optional to support partial data from reservation token payload
   */
  offer?: Partial<TvInternetOffer>;

  /**
   * Top-level provider
   * Uses ServiceabilityProvider from GraphQL schema
   */
  provider?: Partial<ServiceabilityProvider>;

  // Additional top-level fields that may exist in authenticated mode
  pricing?: {
    centralRegion?: boolean;
  };
}
/**
 * BuyFlowContext value interface
 * Provides data for dual-mode (guest + authenticated) buy-flow operation
 */
export interface BuyFlowContextValue {
  /** Guest config from reservation token. Will be undefined in authenticated mode. */
  guestConfig?: GuestConfig;
  /** Reservation data from reservation token (guest) or GraphQL (authenticated) */
  reservation: Reservation;
  /** Installation address */
  installAddress: Address;
  /** Customer information from reservation token (guest) or user profile (authenticated) */
  customerInfo?: CustomerInfo;
  /** Operating mode: guest (from reservation token) or authenticated (logged in user) */
  isGuestMode?: boolean;
  /** Unique identifier: reservation UUID for guest, moveId for authenticated */
  identifier?: string;
  /** Activation defaults for installation scheduling */
  activationDefaults?: ActivationDefaultsType;
  /** Credit check data (Xfinity only, authenticated mode) */
  creditCheckData?: CreditCheckData;
  /** Base path for the buy flow */
  basePath: string;
  /** Lead ID from createLead mutation (guest mode) */
  leadId?: string;
  /** Function to set lead ID after createLead mutation */
  setLeadId?: (leadId: string) => void;
}

/**
 * Props for BuyFlowProvider
 */
export interface BuyFlowProviderProps extends BuyFlowContextValue {
  children: ReactNode;
}

/**
 * Context for dual-mode buy-flow data
 * Supports both guest users (from reservation token) and authenticated users
 */
export const BuyFlowContext = createContext<BuyFlowContextValue | undefined>(
  undefined
);

/**
 * Provider component for buy-flow context
 * Wraps the buy-flow UI and provides reservation, address, and customer data
 *
 * @example
 * // Guest mode
 * <BuyFlowProvider
 *   reservation={guestReservation}
 *   installAddress={installAddress}
 *   customerInfo={customerInfo}
 *   mode="guest"
 *   identifier={reservationId}
 * >
 *   <BuyFlow />
 * </BuyFlowProvider>
 *
 * @example
 * // Authenticated mode
 * <BuyFlowProvider
 *   reservation={authReservation}
 *   installAddress={user.currentMove.toAddress}
 *   customerInfo={userInfo}
 *   mode="authenticated"
 *   identifier={user.currentMove.id}
 * >
 *   <BuyFlow />
 * </BuyFlowProvider>
 */
export function BuyFlowProvider({
  children,
  guestConfig,
  reservation,
  installAddress,
  customerInfo,
  isGuestMode,
  identifier,
  activationDefaults,
  creditCheckData,
  basePath,
}: BuyFlowProviderProps) {
  const contextValue = useMemo(
    () => ({
      guestConfig,
      reservation,
      installAddress,
      customerInfo,
      isGuestMode,
      identifier,
      activationDefaults,
      creditCheckData,
      basePath,
    }),
    [
      guestConfig,
      reservation,
      installAddress,
      customerInfo,
      isGuestMode,
      identifier,
      activationDefaults,
      creditCheckData,
      basePath,
    ]
  );

  return (
    <BuyFlowContext.Provider value={contextValue}>
      {children}
    </BuyFlowContext.Provider>
  );
}

/**
 * Hook to access buy-flow context
 * Throws error if used outside BuyFlowProvider
 *
 * @returns BuyFlowContextValue with reservation, address, and customer data
 *
 * @example
 * function MyComponent() {
 *   const { reservation, isGuestMode, customerInfo } = useBuyFlowContext();
 *
 *   if (isGuestMode) {
 *     // Handle guest-specific logic
 *   }
 * }
 */
export function useBuyFlowContext(): BuyFlowContextValue {
  const context = useContext(BuyFlowContext);

  if (!context) {
    throw new Error('useBuyFlowContext must be used within a BuyFlowProvider');
  }

  return context;
}

/**
 * Optional hook that returns context value or undefined if not available
 * Useful for components that can work with or without the context
 *
 * @returns BuyFlowContextValue or undefined
 *
 * @example
 * function MyComponent() {
 *   const context = useBuyFlowContextOptional();
 *
 *   if (context?.isGuestMode) {
 *     // Use context data
 *   } else {
 *     // Fallback to other data source
 *   }
 * }
 */
export function useBuyFlowContextOptional(): BuyFlowContextValue | undefined {
  return useContext(BuyFlowContext);
}
